import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/api';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Settings, Code, FileText, MessageSquare, Brain, Sparkles, BookOpen } from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';

interface AIScenario {
  id: string;
  scenario_code: string;
  scenario_name: string;
  description?: string;
  prompt_template: string;
  model_vendor_id?: string;
  model_name?: string;
  temperature?: number;
  max_tokens?: number;
  top_p?: number;
  frequency_penalty?: number;
  presence_penalty?: number;
  created_at: string;
  updated_at: string;
}

interface ModelVendor {
  id: string;
  vendor_name: string;
  description?: string;
  is_openai_compatible: boolean;
  api_base_url?: string;
  model_list: any[];
  status: string;
  priority: number;
  is_default: boolean;
}

interface FormData {
  scenario_code: string;
  scenario_name: string;
  description: string;
  prompt_template: string;
  model_vendor_id: string;
  model_name: string;
  temperature: number;
  max_tokens: number;
  top_p: number;
  frequency_penalty: number;
  presence_penalty: number;
}

const AIScenarioEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [scenario, setScenario] = useState<AIScenario | null>(null);
  const [vendors, setVendors] = useState<ModelVendor[]>([]);
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [formData, setFormData] = useState<FormData>({
    scenario_code: '',
    scenario_name: '',
    description: '',
    prompt_template: '',
    model_vendor_id: '',
    model_name: '',
    temperature: 0.7,
    max_tokens: 2048,
    top_p: 1.0,
    frequency_penalty: 0,
    presence_penalty: 0
  });

  const scenarioTypes = [
    { value: 'deep_insight', label: '深度分析', icon: Brain, color: 'blue' },
    { value: 'content_summary', label: '内容摘要', icon: FileText, color: 'green' },
    { value: 'learning_qa', label: '学习问答', icon: MessageSquare, color: 'purple' },
    { value: 'code_review', label: '代码审查', icon: Code, color: 'orange' },
    { value: 'translation', label: '多语言翻译', icon: Sparkles, color: 'pink' },
    { value: 'knowledge_generation', label: '知识生成', icon: BookOpen, color: 'indigo' }
  ];

  useEffect(() => {
    fetchVendors();
    if (id && id !== 'new') {
      fetchScenario();
    } else {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (formData.model_vendor_id) {
      const selectedVendor = vendors.find(v => v.id === formData.model_vendor_id);
      if (selectedVendor && selectedVendor.model_list) {
        setAvailableModels(selectedVendor.model_list);
        if (!selectedVendor.model_list.find(m => m.name === formData.model_name)) {
          setFormData(prev => ({ ...prev, model_name: '' }));
        }
      } else {
        setAvailableModels([]);
        setFormData(prev => ({ ...prev, model_name: '' }));
      }
    } else {
      setAvailableModels([]);
      setFormData(prev => ({ ...prev, model_name: '' }));
    }
  }, [formData.model_vendor_id, vendors]);

  const fetchVendors = async () => {
    try {
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=vendors&action=list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        const vendorData = result.data.data || [];
        setVendors(vendorData.filter((v: ModelVendor) => v.status === 'active'));
      } else {
        console.error('获取厂商失败:', result.error);
        toast.error('获取厂商失败');
      }
    } catch (error) {
      console.error('获取厂商失败:', error);
      toast.error('获取厂商失败');
    }
  };

  const fetchScenario = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=scenarios&action=detail&id=${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        const scenarioData = result.data;
        setScenario(scenarioData);
        setFormData({
          scenario_code: scenarioData.scenario_code,
          scenario_name: scenarioData.scenario_name,
          description: scenarioData.description || '',
          prompt_template: scenarioData.prompt_template,
          model_vendor_id: scenarioData.model_vendor_id || '',
          model_name: scenarioData.model_name || '',
          temperature: scenarioData.temperature || 0.7,
          max_tokens: scenarioData.max_tokens || 2048,
          top_p: scenarioData.top_p || 1.0,
          frequency_penalty: scenarioData.frequency_penalty || 0,
          presence_penalty: scenarioData.presence_penalty || 0
        });
      } else {
        console.error('获取AI场景失败:', result.error);
        toast.error('获取AI场景失败');
        navigate('/admin/ai');
      }
    } catch (error) {
      console.error('获取AI场景失败:', error);
      toast.error('获取AI场景失败');
      navigate('/admin/ai');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);
      
      const isNew = !id || id === 'new';
      const url = isNew 
        ? `${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=scenarios&action=create`
        : `${API_CONFIG.FUNCTIONS_URL}/ai-admin-manage?module=scenarios&action=update`;
      
      const method = isNew ? 'POST' : 'PUT';
      const body = isNew 
        ? formData
        : { id, ...formData };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error);
      }

      toast.success(isNew ? 'AI场景创建成功' : 'AI场景更新成功');
      navigate('/admin/ai');
    } catch (error) {
      console.error('保存AI场景失败:', error);
      toast.error('保存失败：' + (error as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const getTypeInfo = (scenarioCode: string) => {
    return scenarioTypes.find(t => t.value === scenarioCode) || {
      value: scenarioCode,
      label: scenarioCode,
      icon: Settings,
      color: 'gray'
    };
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="ml-4 text-gray-600">正在加载...</p>
        </div>
      </AdminLayout>
    );
  }

  const typeInfo = getTypeInfo(formData.scenario_code);
  const TypeIcon = typeInfo.icon;
  const isNew = !id || id === 'new';

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-6">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/admin/ai')}
                  className="flex items-center text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  返回列表
                </button>
                <div className="h-6 border-l border-gray-300"></div>
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg bg-${typeInfo.color}-100`}>
                    <TypeIcon className={`w-6 h-6 text-${typeInfo.color}-600`} />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                      {isNew ? '创建AI场景' : '编辑AI场景'}
                    </h1>
                    {!isNew && (
                      <p className="text-sm text-gray-500">
                        {scenario?.scenario_name} - {typeInfo.label}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                {submitting ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* 基本信息 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">基本信息</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    场景编码 *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.scenario_code}
                    onChange={(e) => setFormData({ ...formData, scenario_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="例如: deep_insight"
                  />
                  <p className="text-xs text-gray-500 mt-1">用于系统调用的唯一标识符</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    场景名称 *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.scenario_name}
                    onChange={(e) => setFormData({ ...formData, scenario_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="请输入场景名称"
                  />
                </div>
              </div>
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  场景描述
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="请输入场景描述"
                />
              </div>
            </div>

            {/* 模型配置 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">模型配置</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    模型厂商 *
                  </label>
                  <select
                    value={formData.model_vendor_id}
                    onChange={(e) => setFormData({ ...formData, model_vendor_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">请选择模型厂商</option>
                    {vendors.map(vendor => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.vendor_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    模型名称 *
                  </label>
                  <select
                    value={formData.model_name}
                    onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                    disabled={!formData.model_vendor_id || availableModels.length === 0}
                  >
                    <option value="">请选择模型</option>
                    {availableModels.map(model => (
                      <option key={model.name} value={model.name}>
                        {model.display_name || model.name}
                      </option>
                    ))}
                  </select>
                  {formData.model_vendor_id && availableModels.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">该厂商暂无可用模型</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    温度参数 (0-2)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="2"
                    step="0.1"
                    value={formData.temperature}
                    onChange={(e) => setFormData({ ...formData, temperature: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    最大Token数
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="8192"
                    value={formData.max_tokens}
                    onChange={(e) => setFormData({ ...formData, max_tokens: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Top-P参数 (0-1)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={formData.top_p}
                    onChange={(e) => setFormData({ ...formData, top_p: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    频率惩罚 (-2到2)
                  </label>
                  <input
                    type="number"
                    min="-2"
                    max="2"
                    step="0.1"
                    value={formData.frequency_penalty}
                    onChange={(e) => setFormData({ ...formData, frequency_penalty: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    存在惩罚 (-2到2)
                  </label>
                  <input
                    type="number"
                    min="-2"
                    max="2"
                    step="0.1"
                    value={formData.presence_penalty}
                    onChange={(e) => setFormData({ ...formData, presence_penalty: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* 提示词模板 */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">提示词模板</h2>
              <div className="space-y-4">
                <div data-color-mode="light">
                  <MDEditor
                    value={formData.prompt_template}
                    onChange={(value) => setFormData({ ...formData, prompt_template: value || '' })}
                    preview="edit"
                    hideToolbar={false}
                    visibleDragBar={false}
                    height={500}
                    data-testid="md-editor"
                  />
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">变量占位符说明：</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• 使用 <code className="bg-blue-100 px-1 rounded">{'{{variable}}'}</code> 格式定义变量</li>
                    <li>• 使用 <code className="bg-blue-100 px-1 rounded">{'{{variable:default}}'}</code> 格式设置默认值</li>
                    <li>• 例如：<code className="bg-blue-100 px-1 rounded">{'{{content}}'}</code>、<code className="bg-blue-100 px-1 rounded">{'{{max_length:200}}'}</code></li>
                  </ul>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AIScenarioEdit;